import type { FC } from 'react';
import { Suspense, useMemo } from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import type { AssignedUsersParams } from 'src/apis/assignment-service/types';
import { useAssignedShippers } from 'src/apis/assignment-service/use-assigned-shippers';
import { useAssignedUsers } from 'src/apis/assignment-service/use-assigned-users';
import {
  useAssignmentServicePublic,
  type AssignmentServiceRequest,
} from 'src/apis/assignment-service/use-assignment-service-root';
import type { ContractManagementRequest } from 'src/apis/contract-management/use-contract-management';
import { useContractManagement } from 'src/apis/contract-management/use-contract-management';
import type { ContractManagementShipperRequest } from 'src/apis/contract-management/use-contract-management-shipper';
import { useContractManagementShipper } from 'src/apis/contract-management/use-contract-management-shipper';
import { useOptionalShipperById } from 'src/apis/monolith/use-optional-shipper-by-id';
import type { ShipperPlatformIdsRequest } from 'src/apis/monolith/use-shipper-platform-ids';
import { useShipperPlatformIds } from 'src/apis/monolith/use-shipper-platform-ids';
import { useAssignedShipperDetails } from 'src/apis/organisation-service/use-assigned-shipper-details';
import {
  useOrganisationService,
  type OrganisationServiceRequest,
} from 'src/apis/organisation-service/use-organisation-service';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { PageHeader } from 'src/components/layout/page-header';
import { Stack } from 'src/components/layout/stack';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { isNetworkError } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useReferrer } from 'src/hooks/use-referrer';
import { FinancialSecurityDetails } from 'src/pages/operators/assignments/details/financial-securities/details';
import { Platform } from 'src/pages/platform';
import { ContactDetails } from 'src/pages/shippers/details/contact-details';
import { ShipperContracts } from 'src/pages/shippers/details/contracts';
import { ShippersContractDetails } from 'src/pages/shippers/details/contracts/contract-details';
import { EditCreditLimit } from 'src/pages/shippers/details/contracts/edit-credit-limit';
import { ShipperDocuments } from 'src/pages/shippers/details/documents';
import { ShipperFinancialSecurities } from 'src/pages/shippers/details/financial-securities';
import { FinancialSecurityAmendmentApproval } from 'src/pages/shippers/details/financial-securities/amendment-approval';
import { ShipperInformation } from 'src/pages/shippers/details/information';
import { ShipperTabs } from 'src/pages/shippers/details/shipper-tabs';
import { useAssignedUsersPageParams } from 'src/pages/shippers/organisations-and-users/use-page-params';
import { Forbidden } from 'src/utils/errors';
import { ParamsGuard } from 'src/utils/params-guard';

export const ShipperDetails: FC = () => {
  return (
    <RootRoutes>
      {/* old workaround for TAG-4209*/}
      <Route
        path="/mail/shippers/:shipperUuid/financial-securities/:financialSecurityType/:financialSecurityId/*"
        element={
          <ParamsGuard
            name="financialSecurityType"
            regExp={
              /^(cash-deposit|bank-guarantee|rating|parent-company-guarantee)$/
            }
          >
            <RedirectMailFinancialSecurity />
          </ParamsGuard>
        }
      />

      <Route
        path="/dashboard/shippers/:shipperUuid/financial-securities/cash-deposit/:financialSecurityId/amendment/approve/*"
        element={<FinancialSecurityAmendmentApproval type="CASH_DEPOSIT" />}
      />
      <Route
        path="/dashboard/shippers/:shipperUuid/financial-securities/bank-guarantee/:financialSecurityId/amendment/approve/*"
        element={<FinancialSecurityAmendmentApproval type="BANK_GUARANTEE" />}
      />
      <Route
        path="/dashboard/shippers/:shipperUuid/financial-securities/rating/:financialSecurityId/amendment/approve/*"
        element={<FinancialSecurityAmendmentApproval type="RATING" />}
      />
      <Route
        path="/dashboard/shippers/:shipperUuid/financial-securities/parent-company-guarantee/:financialSecurityId/amendment/approve/*"
        element={
          <FinancialSecurityAmendmentApproval type="PARENT_COMPANY_GUARANTEE" />
        }
      />

      <Route
        path="/dashboard/shippers/:shipperUuid/financial-securities/cash-deposit/:financialSecurityId/*"
        element={<FinancialSecurityDetails type="CASH_DEPOSIT" />}
      />
      <Route
        path="/dashboard/shippers/:shipperUuid/financial-securities/rating/:financialSecurityId/*"
        element={<FinancialSecurityDetails type="RATING" />}
      />
      <Route
        path="/dashboard/shippers/:shipperUuid/financial-securities/bank-guarantee/:financialSecurityId/*"
        element={<FinancialSecurityDetails type="BANK_GUARANTEE" />}
      />
      <Route
        path="/dashboard/shippers/:shipperUuid/financial-securities/parent-company-guarantee/:financialSecurityId/*"
        element={<FinancialSecurityDetails type="PARENT_COMPANY_GUARANTEE" />}
      />

      <Route
        path="/dashboard/shippers/:shipperUuid/*"
        element={<LoadShipperId />}
      />

      <Route
        path="/shippers/:shipperRefId/credit-limits"
        element={<RedirectCreditLimits />}
      />

      <Route
        path="/shippers/:shipperRefId/*"
        element={<RedirectShipperPages />}
      />

      <Route path="*" element={<ThrowNotFound />} />
    </RootRoutes>
  );
};

const LoadShipperId = () => {
  const { shipperUuid } = useParams<{ shipperUuid: string }>();
  const shipperPlatformIds = useShipperPlatformIds(shipperUuid);

  if (shipperPlatformIds.response === null) return <PageSpinner />;

  return <RoutesWithShipper shipperPlatformIds={shipperPlatformIds} />;
};

const RoutesWithShipper: FC<{
  shipperPlatformIds?: Successful<ShipperPlatformIdsRequest>;
}> = ({ shipperPlatformIds }) => {
  const contractManagement = useContractManagement();
  const organisationService = useOrganisationService();
  if (
    (!contractManagement.response &&
      !isNetworkError(contractManagement.error)) ||
    !organisationService.response
  )
    return <PageSpinner />;

  return (
    <RootRoutes>
      <Route
        path="/dashboard/shippers/:shipperUuid/*"
        element={
          contractManagement.response?.data._links.shipper ? (
            <LoadContractManagementShipper
              contractManagement={contractManagement}
              shipperPlatformIds={shipperPlatformIds}
            />
          ) : (
            <PagesWithSidebar
              contractManagement={contractManagement}
              shipperPlatformIds={shipperPlatformIds}
              organisationService={organisationService}
            />
          )
        }
      />

      <Route path="*" element={<ThrowNotFound />} />
    </RootRoutes>
  );
};

type LoadContractManagementShipperProps = {
  contractManagement: ContractManagementRequest;
  shipperPlatformIds?: Successful<ShipperPlatformIdsRequest>;
};

const LoadContractManagementShipper: FC<LoadContractManagementShipperProps> = ({
  contractManagement,
  shipperPlatformIds,
}) => {
  const { shipperUuid } = useParams<{ shipperUuid: string }>();

  const contractManagementShipper = useContractManagementShipper({
    shipperUuid,
    shipperUrl: contractManagement.response!.data._links.shipper!.href,
  });
  const organisationService = useOrganisationService();

  if (
    !contractManagementShipper.response?.data ||
    contractManagementShipper.pending ||
    !organisationService.response
  )
    return <PageSpinner />;

  return (
    <PagesWithSidebar
      contractManagement={contractManagement}
      contractManagementShipper={contractManagementShipper}
      shipperPlatformIds={shipperPlatformIds}
      organisationService={organisationService}
    />
  );
};

type PagesWithSidebarProps = {
  contractManagement: ContractManagementRequest;
  contractManagementShipper?: ContractManagementShipperRequest;
  shipperPlatformIds?: Successful<ShipperPlatformIdsRequest>;
  organisationService: Successful<OrganisationServiceRequest>;
};

const PagesWithSidebar: FC<PagesWithSidebarProps> = ({
  contractManagement,
  contractManagementShipper,
  shipperPlatformIds,
  organisationService,
}) => {
  const { minTablet } = useBreakpoints();
  const { shipperUuid } = useParams<{ shipperUuid: string }>();

  if (!organisationService.response.data._links.getAssignedShipperDetails)
    throw new Forbidden();

  const assignmentService = useAssignmentServicePublic();

  const assignedShipperDetails = useAssignedShipperDetails({
    assignedShipperDetailsUrl:
      organisationService.response.data._links.getAssignedShipperDetails.href,
    shipperUuid,
  });

  const referrer = useReferrer({
    label: 'Organisation',
    location: '/shippers/organisations',
  });
  if (!assignedShipperDetails.response || !assignmentService.response)
    return <PageSpinner />;

  const shipperName =
    assignedShipperDetails.response.data.generalInformation.companyName;
  return (
    <Stack gap={1}>
      <PageHeader title={shipperName} backLink={referrer.backLink} />

      <ShipperTabs
        contractManagement={contractManagement.response?.data}
        contractManagementShipper={contractManagementShipper?.response?.data}
        shipperPlatformIds={shipperPlatformIds}
        organisationService={organisationService}
      />

      <Suspense fallback={<PageSpinner />}>
        <RootRoutes>
          <Route
            path="/dashboard/shippers/:shipperUuid/contracts/:contractType/:contractId/details/*"
            element={
              <ParamsGuard
                name="contractType"
                regExp={/^(fmct|dect|lect|fmct-offer|dect-offer)$/}
              >
                <ShippersContractDetails />
              </ParamsGuard>
            }
          />

          <Route
            path="/dashboard/shippers/:shipperUuid/admins/*"
            element={
              <RedirectShippersOverview assignmentService={assignmentService} />
            }
          />

          <Route
            path="/dashboard/shippers/:shipperUuid/contracts/lect/:contractId/edit-credit-limit/*"
            element={<EditCreditLimit />}
          />

          <Route
            path="/dashboard/shippers/:shipperUuid/contracts/*"
            element={
              <ShipperContracts
                shipperName={shipperName}
                contractManagement={contractManagement}
              />
            }
          />

          <Route
            path="/dashboard/shippers/:shipperUuid/documents/*"
            element={
              <ShipperDocuments
                shipperName={shipperName}
                shipperPlatformIds={shipperPlatformIds}
              />
            }
          />

          <Route
            path="/dashboard/shippers/:shipperUuid/platform"
            element={<Platform />}
          />

          <Route
            path="/dashboard/shippers/:shipperUuid/contact-details"
            element={
              <ContactDetails assignedShipperDetails={assignedShipperDetails} />
            }
          />

          <Route
            path="/dashboard/shippers/:shipperUuid/information/*"
            element={
              <ShipperInformation
                contractManagement={contractManagement}
                shipperPlatformIds={shipperPlatformIds}
                organisationService={organisationService}
                assignedShipperDetails={assignedShipperDetails}
                assignmentService={assignmentService}
                shipperUuid={shipperUuid}
              />
            }
          />

          <Route
            path="/dashboard/shippers/:shipperUuid/financial-securities/*"
            element={
              <ShipperFinancialSecurities
                shipperName={shipperName}
                contractManagementShipper={contractManagementShipper}
              />
            }
          />

          <Route
            path="/dashboard/shippers/:shipperUuid"
            element={
              <Navigate
                replace
                to={`/dashboard/shippers/${shipperUuid}/information`}
              />
            }
          />

          <Route path="*" element={<ThrowNotFound />} />
        </RootRoutes>
      </Suspense>
    </Stack>
  );
};

const RedirectCreditLimits: FC = () => {
  const { shipperRefId } = useParams<{ shipperRefId: string }>();
  const shipper = useOptionalShipperById(shipperRefId);
  const { search } = useLocation();

  if (!shipper?.error && !shipper?.response) return <PageSpinner />;
  if (shipper.error) throw shipper.error;

  return (
    <Navigate
      replace
      to={{
        pathname: `/dashboard/shippers/${shipper.response.data.organizationId}/platform/`,
        hash: `/credit-limits/${shipperRefId}${search}`,
      }}
    />
  );
};

const RedirectShipperPages: FC = () => {
  const { shipperRefId } = useParams<{ shipperRefId: string }>();
  const { pathname, search, state } = useLocation();
  const shipper = useOptionalShipperById(shipperRefId);
  if (!shipper?.loaded) return <PageSpinner />;

  if (!shipper.response)
    return (
      <Navigate
        replace
        to={{
          pathname: '/shippers/organisations-and-users',
          search,
        }}
        state={state}
      />
    );

  return (
    <Navigate
      replace
      to={{
        pathname: pathname.replace(
          `/shippers/${shipperRefId}`,
          `/dashboard/shippers/${shipper.response.data.organizationId}`
        ),
        search,
      }}
      state={state}
    />
  );
};

const RedirectMailFinancialSecurity: FC = () => {
  const { shipperUuid, financialSecurityType, financialSecurityId } =
    useParams<{
      shipperUuid: string;
      financialSecurityType:
        | 'cash-deposit'
        | 'bank-guarantee'
        | 'rating'
        | 'parent-company-guarantee';
      financialSecurityId: string;
    }>();

  return (
    <Navigate
      replace
      to={`/dashboard/shippers/${shipperUuid}/financial-securities/${financialSecurityType}/${financialSecurityId}`}
    />
  );
};

const RedirectShippersOverview: FC<{
  assignmentService: Successful<AssignmentServiceRequest>;
}> = ({ assignmentService }) => {
  const { shipperUuid } = useParams<{ shipperUuid: string }>();
  if (!assignmentService.response.data._links.assignedUsers)
    throw new Forbidden();

  const pageParams = useAssignedUsersPageParams();
  const params = useMemo(() => {
    return {
      sortBy: pageParams.value.sortBy,
      sortDirection: pageParams.value.sortDirection,
      limit: pageParams.value.pageSize,
      offset: pageParams.value.start,
      name: pageParams.value.name,
      shipperId: pageParams.value.shipperId?.value,
      email: pageParams.value.email,
      role: pageParams.value.role,
      userAssignmentStatus: pageParams.value.userAssignmentStatus,
      organisationAssignmentStatus:
        pageParams.value.organisationAssignmentStatus,
      userStatus: pageParams.value.userStatus,
      tradingMarket: pageParams.value.tradingMarket,
    } satisfies AssignedUsersParams;
  }, [pageParams.value]);

  const assignedUsers = useAssignedUsers({
    url: assignmentService.response.data._links.assignedUsers.href,
    params,
  });

  const assignedShippers = useAssignedShippers({
    assignedUsers,
  });

  if (!assignedShippers.response) return <PageSpinner />;

  const shipper = assignedShippers.response.data._embedded.items.filter(
    (shipper) => shipper.shipperId === shipperUuid
  )[0];

  return (
    <Navigate
      replace
      to={{
        pathname: `/shippers/organisations-and-users`,
        search: `role=ADMIN&shipperId[label]=${encodeURIComponent(shipper.shipperName)}&shipperId[value]=${shipper.shipperId}${shipper.shipperEic ? `&shipperId[hint]=${encodeURIComponent(shipper.shipperEic)}` : ''}`,
      }}
    />
  );
};
